import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { apolloClient } from '@client/api/graphql/client';
import {
  DeleteReplyDocument,
  DeleteReplyMutation,
  DeleteReplyMutationVariables,
  Post,
  ReactionType,
  Reply,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';
import toast from 'react-hot-toast';

type PostsCache = InfiniteData<Post[] | undefined> | undefined;
type PostCache = Post | undefined;
type CommentsCache = InfiniteData<Reply[]> | undefined;

type RollbackContext = {
  previousPosts: PostsCache;
  previousPost: PostCache;
  previousComments: CommentsCache;
};

const decrementCommentCount = (post: Post): Post => ({
  ...post,
  reactions: post.reactions
    .map((r) =>
      r.type === ReactionType.Comment ? { ...r, count: r.count - 1 } : r,
    )
    .filter((r) => r.count > 0),
});

const applyOptimisticDecrementOnList =
  (postId: string) =>
  (cache: PostsCache): PostsCache => {
    if (!cache) return cache;
    return {
      ...cache,
      pages: cache.pages.map((page) =>
        page?.map((post) =>
          post.id === postId ? decrementCommentCount(post) : post,
        ),
      ),
    };
  };

const applyOptimisticDecrementOnPost = (cache: PostCache): PostCache =>
  cache ? decrementCommentCount(cache) : cache;

const removeReplyFromComments =
  (replyId: string) =>
  (cache: CommentsCache): CommentsCache => {
    if (!cache) return cache;
    return {
      ...cache,
      pages: cache.pages.map((page) => page.filter((r) => r.id !== replyId)),
    };
  };

export const useDeleteReplyMutation = (communityId: number, postId: string) => {
  const queryClient = useQueryClient();
  const postsKey = communityQueryKeys.posts(communityId);
  const postKey = communityQueryKeys.post(communityId, postId);
  const postCommentsKey = communityQueryKeys.postComments(communityId, postId);

  return useMutation<unknown, Error, string, RollbackContext>({
    mutationFn: (replyId) =>
      apolloClient.mutate<DeleteReplyMutation, DeleteReplyMutationVariables>({
        mutation: DeleteReplyDocument,
        variables: { communityId, replyId },
      }),
    onMutate: async (replyId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: postsKey }),
        queryClient.cancelQueries({ queryKey: postKey }),
        queryClient.cancelQueries({ queryKey: postCommentsKey }),
      ]);

      const previousPosts = queryClient.getQueryData<PostsCache>(postsKey);
      const previousPost = queryClient.getQueryData<PostCache>(postKey);
      const previousComments =
        queryClient.getQueryData<CommentsCache>(postCommentsKey);

      queryClient.setQueryData<PostsCache>(
        postsKey,
        applyOptimisticDecrementOnList(postId),
      );
      queryClient.setQueryData<PostCache>(
        postKey,
        applyOptimisticDecrementOnPost,
      );
      queryClient.setQueryData<CommentsCache>(
        postCommentsKey,
        removeReplyFromComments(replyId),
      );

      return { previousPosts, previousPost, previousComments };
    },
    onSuccess: () => {
      toast.success('Your comment was successfully deleted!');
      queryClient.refetchQueries({ queryKey: postCommentsKey });
      queryClient.refetchQueries({ queryKey: postKey });
    },
    onError: (error, _replyId, context) => {
      console.error({ error });
      if (context) {
        queryClient.setQueryData<PostsCache>(postsKey, context.previousPosts);
        queryClient.setQueryData<PostCache>(postKey, context.previousPost);
        queryClient.setQueryData<CommentsCache>(
          postCommentsKey,
          context.previousComments,
        );
      }
      toast.error('Something went wrong');
    },
  });
};
